import React, {useState, useEffect} from 'react'
import StatusDropdownDirectory from './StatusDropdownDirectory'
import SafeIcon from '../../common/SafeIcon'
import DirectoryContentGenerator from './DirectoryContentGenerator'
import * as FiIcons from 'react-icons/fi'
import { SupabaseService } from '../../services/supabaseService'

const {FiExternalLink, FiMail, FiFlag, FiStar, FiMinusCircle, FiPlusCircle} = FiIcons

// Type color mapping
const typeColors = {
  'Guest Post': 'bg-green-100 text-green-800',
  'Resource Page': 'bg-blue-100 text-blue-800',
  'Blog Comment': 'bg-purple-100 text-purple-800',
  'Forum Comment': 'bg-orange-100 text-orange-800',
  'Directory Submission': 'bg-yellow-100 text-yellow-800',
  'Podcast': 'bg-pink-100 text-pink-800',
  'Sponsored Post': 'bg-indigo-100 text-indigo-800',
  'Infographic Submission': 'bg-red-100 text-red-800',
  'Interview Opportunity': 'bg-cyan-100 text-cyan-800',
  'Product Review': 'bg-lime-100 text-lime-800',
  'Link Roundup': 'bg-amber-100 text-amber-800',
  'Broken Link Building': 'bg-teal-100 text-teal-800',
  // Default for any other types
  'default': 'bg-gray-100 text-gray-800'
}

const LinkDirectoryRow = ({opportunity, user, onStatusUpdate, onReportBrokenLink, onFavorite, onStatusChange, userActions={}, onPurchaseClick, defaultValues, onSendEmail}) => {
  const [collapsed, setCollapsed] = useState(!(userActions[opportunity.id]?.is_open ?? true))

  useEffect(() => {
    // Sync with userActions updates
    if (Object.prototype.hasOwnProperty.call(userActions, opportunity.id)) {
      setCollapsed(!(userActions[opportunity.id]?.is_open ?? true))
    }
  }, [userActions, opportunity.id])

  const isEmail = (contact) => {
    return contact && contact.includes('@') && !contact.startsWith('http')
  }

  const handleContactClick = (contact) => {
    if (isEmail(contact)) {
      window.open(`mailto:${contact}`, '_blank', 'noopener,noreferrer')
    } else {
      window.open(contact, '_blank', 'noopener,noreferrer')
    }
  }

  // Get color for type
  const getTypeColor = (type) => {
    return typeColors[type] || typeColors.default
  }

  // Check if opportunity is favorited
  const isFavorited = userActions[opportunity.id]?.favorited || false

  // Get status from user actions if available, otherwise use opportunity status
  const displayStatus = userActions[opportunity.id]?.status || opportunity.status || 'New'

  const toggleCollapse = async () => {
    const next = !collapsed
    setCollapsed(next)
    // Persist state: is_open true if expanded (not collapsed)
    const isOpen = !next
    SupabaseService.saveLinkOpenState(user.id, opportunity.id, isOpen).catch(() => {})
  }

  return (
    <div className="p-6 dark:border-gray-700">
      <div className="grid grid-cols-1 md:grid-cols-8 gap-4 items-start">
        {/* State */}
        <div className="md:col-span-1" style={{width: '50%'}}>
          <div className="text-sm text-gray-600 dark:text-gray-400 md:hidden font-medium mb-1">State:</div>
          <span className="inline-block px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-full text-sm">
            {opportunity.state_code || ''}
          </span>
        </div>

        {/* Niche */}
        <div className="md:col-span-1">
          <div className="text-sm text-gray-600 dark:text-gray-400 md:hidden font-medium mb-1">Niche:</div>
          <span className="inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 rounded-full text-sm">
            {opportunity.niche}
          </span>
        </div>

        {/* Type */}
        <div className="md:col-span-1">
          <div className="text-sm text-gray-600 dark:text-gray-400 md:hidden font-medium mb-1">Type:</div>
          <span className={`inline-block px-3 py-1 rounded-full text-sm ${getTypeColor(opportunity.type)}`}>
            {opportunity.type}
          </span>
        </div>

        {/* Link Opportunity */}
        <div className="md:col-span-2">
          <div className="text-sm text-gray-600 dark:text-gray-400 md:hidden font-medium mb-1">Opportunity:</div>
          <a
            href={opportunity.link_opportunity.startsWith('http') ? opportunity.link_opportunity : `https://${opportunity.link_opportunity}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 break-words"
          >
            {opportunity.link_opportunity}
          </a>
        </div>

        {/* Contact */}
        <div className="md:col-span-1">
          <div className="text-sm text-gray-600 dark:text-gray-400 md:hidden font-medium mb-1">Contact:</div>
          <div
            onClick={() => handleContactClick(opportunity.contact)}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 break-all cursor-pointer"
          >
            <SafeIcon
              icon={isEmail(opportunity.contact) ? FiMail : FiExternalLink}
              className="w-3 h-3 mr-1 flex-shrink-0"
            />
            <span className="truncate max-w-[150px]">
              {opportunity.contact}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="md:col-span-1 flex items-center space-x-2">
          <StatusDropdownDirectory
            key={`${opportunity.id}-${displayStatus}`}
            opportunityId={opportunity.id}
            initialStatus={displayStatus}
            user={user}
            onStatusUpdate={onStatusUpdate}
            onStatusChange={onStatusChange}
          />

          <button
            onClick={() => onFavorite(opportunity)}
            className={`p-2 ${isFavorited ? 'text-yellow-500' : 'text-yellow-300'} hover:text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 rounded-full transition-colors no-border`}
            title={isFavorited ? "Remove from favorites" : "Add to favorites"}
          >
            <SafeIcon icon={FiStar} className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
          </button>

          <button
            onClick={() => onReportBrokenLink(opportunity)}
            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-full transition-colors no-border"
            title="Report broken link"
          >
            <SafeIcon icon={FiFlag} className="w-4 h-4" />
          </button>

          <button
            onClick={toggleCollapse}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 rounded-full transition-colors no-border"
            title={collapsed ? "Expand opportunity" : "Collapse opportunity"}
          >
            <SafeIcon
              icon={collapsed ? FiPlusCircle : FiMinusCircle}
              className="w-4 h-4"
            />
          </button>
        </div>
      </div>

      {/* Content Generator Component - Only shown when not collapsed */}
      {!collapsed && (
        <div className="mt-4">
          <DirectoryContentGenerator
            opportunity={opportunity}
            user={user}
            onPurchaseClick={onPurchaseClick}
            defaultValues={defaultValues}
            onSendEmail={onSendEmail}
            onStatusChange={onStatusChange}
          />
        </div>
      )}
    </div>
  )
}

export default LinkDirectoryRow